import { REPORT_TEMPLATE, type IReportCardInfo, type TechBgCardData } from '@/types';
import type { BaseCardService } from './abstract-base-card-service';
import { BaseCardServiceImpl, type BaseReportCardData } from './abstract-base-card-service';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { ReportApiService } from '@/infrastructure/api-functions/report-api-service';
import { I18nService } from '../i18n-service';
import { ReferenceModelService, type IReferenceModelService } from './reference-model-service';
import _ from 'lodash'

export const TechBgService = Symbol('TechBgService');

export type TechBgService = BaseCardService<TechBgCardData>

@injectable()
export class TechBgServiceImpl extends BaseCardServiceImpl<TechBgCardData> implements TechBgService {
    _modelData = {
        isLoading: false,
        isFailed: false,
        isSuccess: false,
        data: undefined,
    };

    templateType = REPORT_TEMPLATE.TECHNICAL_BACKGROUND;

    constructor(
    @inject(ReportApiService) _reportApiService: ReportApiService,
    @inject(I18nService) private readonly _i18nService: I18nService,
    @inject(ReferenceModelService) _referenceService: IReferenceModelService
    ) {
        super(_reportApiService, _referenceService);
    }

    private _generateTechBgCardData(cardData: BaseReportCardData<IReportCardInfo>) {
        const cardInfo = cardData.data?.[REPORT_TEMPLATE.TECHNICAL_BACKGROUND];
        const gptData = cardInfo?.TECHNICAL_BACKGROUND_GPT || { content: this._i18nService.t('report.noData'), rag_list: [] };
        const patentChartData = _.isEmpty(cardInfo?.TECHNICAL_BACKGROUND_PATENT_CHART) ? undefined : cardInfo?.TECHNICAL_BACKGROUND_PATENT_CHART;
        const paperChartData = _.isEmpty(cardInfo?.TECHNICAL_BACKGROUND_PAPER_CHART) ? undefined : cardInfo?.TECHNICAL_BACKGROUND_PAPER_CHART;

        return {
            description: gptData.content,
            rag_list: gptData.rag_list,
            patentChartData,
            paperChartData,
        }
    }

    updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void {
        const updatedValue = getUpdateValue(_.omit(this.modelData, 'data'));

        const dataHaveFromUpdated = _.has(updatedValue, 'data')

        if (dataHaveFromUpdated) {
            const generatedData = this._generateTechBgCardData(updatedValue);

            this.update(modelData => ({
                ...modelData,
                ...updatedValue,
                data: generatedData,
            }));
            this._autoRegisterReferences(generatedData.rag_list || []);
        } else {
            this.update(m => ({
                ...m,
                ...updatedValue,
                data: m.data,
            }));
        }
    }
}
